import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type TicketsView = "kanban" | "table";

export type TicketsPeriodPreset =
    | "all"
    | "last_quarter"
    | "year_to_date"
    | "previous_year"
    | "custom";

export interface TicketsQueryParams {
    view: TicketsView;
    branchID: string;
    trainNumber: string;
    categoryID: string;
    type: string;
    period: TicketsPeriodPreset;
    from: string;
    to: string;
    page: number;
    limit: number;
}

const DEFAULTS: TicketsQueryParams = {
    view: "table",
    branchID: "",
    trainNumber: "",
    categoryID: "",
    type: "",
    period: "all",
    from: "",
    to: "",
    page: 1,
    limit: 50,
};

const STRING_KEYS: Array<keyof TicketsQueryParams> = [
    "view",
    "branchID",
    "trainNumber",
    "categoryID",
    "type",
    "period",
    "from",
    "to",
];

export const useTicketsQueryParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const value = useMemo<TicketsQueryParams>(() => {
        const view = (searchParams.get("view") as TicketsView) || DEFAULTS.view;
        const period = (searchParams.get("period") as TicketsPeriodPreset) || DEFAULTS.period;
        const pageRaw = parseInt(searchParams.get("page") || "", 10);
        const limitRaw = parseInt(searchParams.get("limit") || "", 10);
        return {
            view: view === "table" ? "table" : "kanban",
            branchID: searchParams.get("branchID") || "",
            trainNumber: searchParams.get("trainNumber") || "",
            categoryID: searchParams.get("categoryID") || "",
            type: searchParams.get("type") || "",
            period: period || "all",
            from: searchParams.get("from") || "",
            to: searchParams.get("to") || "",
            page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : DEFAULTS.page,
            limit: Number.isFinite(limitRaw) && limitRaw > 0 ? limitRaw : DEFAULTS.limit,
        };
    }, [searchParams]);

    const setMany = useCallback(
        (partial: Partial<TicketsQueryParams>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    Object.entries(partial).forEach(([k, v]) => {
                        const key = k as keyof TicketsQueryParams;
                        const isDefault =
                            (key === "view" && v === DEFAULTS.view) ||
                            (key === "period" && v === DEFAULTS.period) ||
                            (key === "page" && v === DEFAULTS.page) ||
                            (key === "limit" && v === DEFAULTS.limit);
                        if (v === undefined || v === null || v === "" || isDefault) {
                            next.delete(key);
                        } else {
                            next.set(key, String(v));
                        }
                    });
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const reset = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                STRING_KEYS.forEach((k) => next.delete(k));
                next.delete("page");
                next.delete("limit");
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    return { value, setMany, reset };
};
